import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { QUERY_KEYS } from 'constants/queryKeys';
import { getCleanedUrlParams, showSuccess, showError } from '@utils';
import { boardsServices } from '@services';
import { BOARDS_ID } from '@constants';

import {
  LOGISTICS_DEFAULT_ORDER,
  LOGISTICS_DEFAULT_ORDER_BY,
  LOGISTICS_PAGE_SIZE,
} from 'components/logistics/constants';
import {
  filtersModalConditionLogisticsChange,
  filtersModalRulesLogisticsChange,
  resetModalLogisticsChange,
  selectLogisticsState,
} from 'store/logistics/logisticsSlice';
import { useServicePoints } from 'hooks/common';
import {
  getLogisticsSortingParams,
  getLogisticsPayload,
  handleLogisticsData,
  handleServicePoints,
} from './utils';
import { useAssignees } from '../common/useAssignees';

const useLogisticsTasksOperations = () => {
  const { query, isReady } = useRouter();
  const {
    servicePointsWithDefaultLocations,
    isFetching: isServicePointsFetching,
    isFetched: isServicePointsFetched,
  } = useServicePoints();
  const dispatch = useDispatch();
  const { filters: modalFilters } = useSelector(selectLogisticsState);

  const {
    page = 1,
    pageSize = LOGISTICS_PAGE_SIZE,
    order = LOGISTICS_DEFAULT_ORDER,
    orderBy = LOGISTICS_DEFAULT_ORDER_BY,
    query: searchQuery,
    ...filters
  } = query;

  const {
    drivers,
    isFetched: isDriversFetched,
    isFetching: isDriversFetching,
  } = useAssignees();

  const {
    data: logisticsData,
    refetch: getLogisticsData,
    isFetching: isLogisticsFetching,
  } = useQuery(
    [
      QUERY_KEYS.GET_LOGISTICS_KEY,
      { ...query, ...modalFilters?.modalFiltersRules },
    ],
    () => {
      const params = {
        page,
        pageSize: parseInt(pageSize, 10) || LOGISTICS_PAGE_SIZE,
        order,
        orderBy,
        query: searchQuery,
      };

      const cleanedParams = getCleanedUrlParams(params);

      const sortingParams = getLogisticsSortingParams(cleanedParams);

      const payload = getLogisticsPayload(
        filters,
        modalFilters?.modalFiltersRules
      );

      return boardsServices.getBoardTasks({
        boardId: BOARDS_ID.LOGISTICS,
        params: sortingParams,
        payload,
      });
    },
    {
      enabled: isReady && isDriversFetched && isServicePointsFetched,
      refetchOnMount: true,
    }
  );

  const currentLocations = useMemo(
    () => [
      {
        text: '-',
        value: '',
      },
      ...servicePointsWithDefaultLocations,
    ],
    [servicePointsWithDefaultLocations]
  );
  // servicePointsWithDefaultLocations with airport and resorted
  const servicePoints = useMemo(
    () => handleServicePoints(servicePointsWithDefaultLocations),
    [servicePointsWithDefaultLocations]
  );

  const logistics = useMemo(
    () =>
      handleLogisticsData({
        data: logisticsData?.data,
        servicePoints,
        currentLocations,
      }),
    [logisticsData, servicePoints, currentLocations]
  );

  const total = logisticsData?.meta?.total;

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_TASK_STATUSES_KEY],
    () => boardsServices.getBoardsEnum(),
    { enabled: isReady }
  );

  const boardEnumsResponse = useQuery(
    [QUERY_KEYS.GET_LOGISTICS_ENUMS_KEY],
    () => boardsServices.getEnumsPerBoardId(BOARDS_ID.LOGISTICS),
    { enabled: isReady }
  );

  const resetModalChange = useCallback(() => {
    dispatch(resetModalLogisticsChange());
  }, [dispatch]);

  const handleFilterModalChange = useCallback(
    (conditions) => {
      dispatch(filtersModalConditionLogisticsChange(conditions));
    },
    [dispatch]
  );
  const handleFiltersChange = (rules) => {
    dispatch(filtersModalRulesLogisticsChange(rules));
  };

  const handleExportRequest = async () => {
    const getLogisticsExportPayload = () => {
      const params = { page, pageSize, order, orderBy, query: searchQuery };

      const cleanedParams = getCleanedUrlParams(params);
      const sortingParams = getLogisticsSortingParams(cleanedParams);

      return {
        boardId: BOARDS_ID.LOGISTICS,
        payload: {
          type: 'csv',
          rule: getLogisticsPayload(filters, modalFilters?.modalFiltersRules),
        },
        params: sortingParams,
      };
    };
    const payload = getLogisticsExportPayload();

    try {
      await boardsServices.exportFileAsync(payload);
      showSuccess('Successfully submitted job');
    } catch (error) {
      showError('Failed to submit job');
    }
  };
  const loading =
    isLogisticsFetching || isDriversFetching || isServicePointsFetching;

  const enums = useMemo(() => {
    const boardEnums = boardEnumsResponse?.data?.data || {};
    const generalEnums = enumsResponse?.data?.data || {};
    return {
      ...generalEnums,
      ...boardEnums,
    };
  }, [enumsResponse, boardEnumsResponse]);

  return {
    total,
    loading,
    logistics,
    assignees: drivers,
    currentLocations,
    servicePoints,
    page,
    pageSize: parseInt(pageSize, 10) || LOGISTICS_PAGE_SIZE,
    filters: modalFilters,
    enums,
    getLogisticsData,
    resetModalChange,
    handleFilterModalChange,
    handleFiltersChange,
    handleExportRequest,
  };
};

export default useLogisticsTasksOperations;
